import React, { type ReactNode } from 'react';
import { GroupRenderer } from './GroupRenderer';
import { RowRenderer, type RowChrome } from './RowRenderer';
import { padStyle, presentationTableProps } from './table';
import { emailRowAreaWidth } from '../domain/column-presets';
import type { FacilityProfile } from '../domain/facility';
import { ZERO_PADDING } from '../domain/create-shared';
import { isGroup, isRow } from '../domain/sections';
import { shouldRenderRow } from '../domain/row-policy';
import type { EmailDocument, EmailSettings, Padding } from '../domain/types';
import type { RenderMergeMode } from './BlockRenderer';

type DocumentRendererProps = {
  document: EmailDocument;
  facility: FacilityProfile;
  mergeMode: RenderMergeMode;
  chrome?: RowChrome;
  shell?: 'full' | 'content';
  editorMode?: boolean;
};

function resolvePagePadding(settings: EmailSettings): Padding {
  return settings.pagePadding ?? ZERO_PADDING;
}

function EditorCanvasShell({
  settings,
  pagePadding,
  children,
}: {
  settings: EmailSettings;
  pagePadding: Padding;
  children: ReactNode;
}) {
  const { contentWidth, contentBackgroundColor } = settings;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: pagePadding.top,
        paddingBottom: pagePadding.bottom,
        ['--editor-content-width' as string]: `${contentWidth}px`,
      }}>
      <div
        data-editor-content-bg
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: contentWidth,
          left: `max(0px, calc(50% - ${contentWidth / 2}px))`,
          backgroundColor: contentBackgroundColor,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
        {children}
      </div>
    </div>
  );
}

function ContentCard({
  settings,
  pagePadding,
  children,
}: {
  settings: EmailSettings;
  pagePadding: Padding;
  children: ReactNode;
}) {
  const { contentWidth, contentBackgroundColor } = settings;
  const inset = padStyle(pagePadding);

  return (
    <div
      className="email-content-card"
      style={{
        width: '100%',
        maxWidth: contentWidth,
        margin: '0 auto',
        ['containerType' as string]: 'inline-size',
        ['containerName' as string]: 'email',
      }}>
      <table
        {...presentationTableProps()}
        {...({ align: 'center' } as React.TableHTMLAttributes<HTMLTableElement>)}
        style={{
          ...presentationTableProps().style,
          width: '100%',
          backgroundColor: contentBackgroundColor,
        }}>
        <tbody>
          <tr>
            <td style={inset}>{children}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function DocumentRenderer({
  document,
  facility,
  mergeMode,
  chrome,
  shell = 'full',
  editorMode = false,
}: DocumentRendererProps) {
  const { settings } = document;
  const pagePadding = resolvePagePadding(settings);
  const rowAreaWidth = emailRowAreaWidth(settings.contentWidth, pagePadding);

  const sections = document.sections.map(section => {
    if (isRow(section)) {
      if (!shouldRenderRow(section, editorMode)) {
        return null;
      }
      return (
        <RowRenderer
          key={section.id}
          row={section}
          contentWidth={editorMode ? settings.contentWidth : rowAreaWidth}
          pagePadding={editorMode ? pagePadding : ZERO_PADDING}
          facility={facility}
          textTheme={settings}
          mergeMode={mergeMode}
          chrome={chrome}
          editorMode={editorMode}
        />
      );
    }
    if (isGroup(section)) {
      return (
        <GroupRenderer
          key={section.id}
          group={section}
          contentWidth={editorMode ? settings.contentWidth : rowAreaWidth}
          pagePadding={editorMode ? pagePadding : ZERO_PADDING}
          facility={facility}
          textTheme={settings}
          mergeMode={mergeMode}
          chrome={chrome}
          editorMode={editorMode}
        />
      );
    }
    return null;
  });

  const content = editorMode ? (
    <EditorCanvasShell settings={settings} pagePadding={pagePadding}>
      {sections}
    </EditorCanvasShell>
  ) : (
    <ContentCard settings={settings} pagePadding={pagePadding}>
      {sections}
    </ContentCard>
  );

  if (shell === 'content') {
    return content;
  }

  return (
    <table
      {...presentationTableProps()}
      style={{
        ...presentationTableProps().style,
        width: '100%',
        backgroundColor: settings.pageBackgroundColor,
      }}>
      <tbody>
        <tr>
          <td
            align={"center" as const}
            style={{ backgroundColor: settings.pageBackgroundColor }}>
            {content}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
